/**
 * TenantSwitcher (B1.3) — Multi-tenant branch picker.
 *
 * Authority:
 *   - ADR-101 (Multi-Tenant RLS): client swap of active branch must trigger
 *     re-resolution on the backend (new JWT claim or branch-scoped session).
 *   - UX Blueprint §3 screen B1.3
 *
 * Behavior:
 *   - Single-branch user → shows branch name (read-only) in the trigger.
 *   - Multi-branch user → opens a Popover list with role chip per branch.
 *   - Switching invalidates all React Query caches (tenant-scoped data is stale).
 *   - Selected branch is persisted in localStorage (`petota-business:active-branch`)
 *     so that the next session starts in the same context.
 *
 * This is a Claude-owned smart wrapper — NOT regenerated by Lovable.
 */
import * as Popover from '@radix-ui/react-popover';
import { useQueryClient } from '@tanstack/react-query';
import { Buildings, CaretDown, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant, type TenantMembership } from '../shared/context/TenantContext';
import { Input } from '../shared/ui/Input';
import { cn } from '../shared/utils/cn';

export function TenantSwitcher({ className }: { className?: string }): JSX.Element | null {
  const { t } = useTranslation('auth');
  const { current, memberships, switchBranch, isResolving } = useTenant();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo<TenantMembership[]>(() => {
    if (!search.trim()) return memberships;
    const q = search.toLowerCase();
    return memberships.filter(
      (m) =>
        m.branchName.toLowerCase().includes(q) ||
        m.organizationName.toLowerCase().includes(q) ||
        m.legalEntityName.toLowerCase().includes(q),
    );
  }, [memberships, search]);

  if (!current) return null;

  const isSingle = memberships.length <= 1;

  const handleSwitch = async (branchId: string) => {
    if (branchId === current.branchId) {
      setOpen(false);
      return;
    }
    await switchBranch(branchId);
    await queryClient.invalidateQueries();
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={isSingle || isResolving}
          aria-label={t('tenantSwitcher.switchButton')}
          className={cn(
            'flex items-center gap-biz-content-lg',
            'min-h-biz-touch px-biz-content-lg',
            'bg-biz-surface text-biz-foreground',
            'border border-biz-border rounded-biz-btn',
            'hover:bg-biz-muted transition-colors duration-biz-fast',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
            'disabled:cursor-default disabled:opacity-100',
            className,
          )}
        >
          <Buildings weight="regular" size={20} aria-hidden="true" className="text-biz-muted-foreground" />
          <div className="flex flex-col items-start text-left min-w-0">
            <span className="text-biz-sm font-semibold truncate max-w-[200px]">
              {current.branchName}
            </span>
            <span className="text-biz-xs text-biz-muted-foreground truncate max-w-[200px]">
              {current.organizationName}
            </span>
          </div>
          {!isSingle ? (
            <CaretDown weight="regular" size={16} aria-hidden="true" className="ml-biz-content text-biz-muted-foreground" />
          ) : null}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className={cn(
            'z-biz-dropdown w-[360px] max-h-[480px] flex flex-col',
            'bg-biz-surface-elevated text-biz-foreground',
            'border border-biz-border rounded-biz-card shadow-biz-lg',
            'data-[state=open]:animate-biz-fade-in',
          )}
        >
          <div className="px-biz-card py-biz-content-lg border-b border-biz-border">
            <h2 className="text-biz-base font-semibold biz-heading">
              {t('tenantSwitcher.title')}
            </h2>
            <p className="mt-1 text-biz-xs text-biz-muted-foreground">
              {t('tenantSwitcher.subtitle')}
            </p>
          </div>

          {memberships.length > 5 ? (
            <div className="px-biz-card py-biz-content border-b border-biz-border">
              <div className="relative">
                <MagnifyingGlass
                  weight="regular"
                  size={18}
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground"
                />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('tenantSwitcher.searchPlaceholder')}
                  className="pl-10"
                  autoFocus
                />
              </div>
            </div>
          ) : null}

          <ul
            className="flex-1 overflow-y-auto py-biz-content"
            role="listbox"
            aria-label={t('tenantSwitcher.title')}
          >
            {filtered.map((m) => {
              const isCurrent = m.branchId === current.branchId;
              return (
                <li key={m.branchId}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => handleSwitch(m.branchId)}
                    className={cn(
                      'w-full flex items-start gap-biz-content-lg',
                      'px-biz-card py-biz-content-lg min-h-biz-touch',
                      'text-left hover:bg-biz-muted',
                      'focus-visible:outline-none focus-visible:bg-biz-muted',
                      'transition-colors duration-biz-fast',
                      isCurrent && 'bg-biz-primary-subtle',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-biz-content">
                        <span className="text-biz-sm font-semibold truncate">
                          {m.branchName}
                        </span>
                        <RoleChip role={m.role} />
                      </div>
                      <div className="mt-1 text-biz-xs text-biz-muted-foreground truncate">
                        {m.organizationName} · {m.legalEntityName}
                      </div>
                    </div>
                    {isCurrent ? (
                      <Check
                        weight="regular"
                        size={20}
                        aria-hidden="true"
                        className="text-biz-primary shrink-0"
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 ? (
              <li className="px-biz-card py-biz-content-lg text-biz-sm text-biz-muted-foreground">
                {t('tenantSwitcher.noMemberships')}
              </li>
            ) : null}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function RoleChip({ role }: { role: TenantMembership['role'] }): JSX.Element {
  const { t } = useTranslation('auth');
  const labelKey =
    role === 'owner'
      ? 'tenantSwitcher.roleOwner'
      : role === 'manager'
        ? 'tenantSwitcher.roleManager'
        : role === 'receptionist'
          ? 'tenantSwitcher.roleReceptionist'
          : role === 'finance'
            ? 'tenantSwitcher.roleFinance'
            : role === 'readonly'
              ? 'tenantSwitcher.roleReadonly'
              : 'tenantSwitcher.roleStaff';
  return (
    <span
      className={cn(
        'inline-flex items-center px-biz-content py-0.5',
        'text-biz-xs font-medium',
        'bg-biz-muted text-biz-muted-foreground rounded-biz-full',
      )}
    >
      {t(labelKey)}
    </span>
  );
}
